import { useEffect, useState } from "react";
import { Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, Trash2, Plus, ArrowUp, ArrowDown, Minus, AlertCircle, Crosshair } from "lucide-react";
import { useMeta } from "@/lib/meta";
import { newLine, maxLines, centerAll } from "@/lib/layout";

const ib = (on, dis) =>
  `press w-8 h-8 inline-flex items-center justify-center rounded border text-sm ${dis ? "opacity-30 cursor-not-allowed" : ""} ${on ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`;

function LineCard({ line, i, n, cfg, fonts, update, remove, moveBy, active, onFocus, warn, fitted }) {
  const f = fonts.find((x) => x.id === line.font) || fonts[0];
  const canBold = f?.bold, canItalic = line.bold ? f?.bold_italic : f?.italic;
  const setSize = (v) => update({ size_pt: Math.max(cfg.min_font_pt, Math.min(cfg.max_font_pt, Math.round(v * 2) / 2)) });
  const empty = !line.text.trim();
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    if (!empty) return setMissing(false);
    const t = setTimeout(() => setMissing(true), 2500);
    return () => clearTimeout(t);
  }, [empty]);
  return (
    <div data-testid={`stamp-line-card-${i}`} onClick={onFocus}
      className={`rounded-lg border p-3 space-y-2.5 transition-colors ${active ? "border-brand-navy bg-brand-yellow/15" : "border-slate-200 bg-white"} ${warn ? "ring-1 ring-amber-400" : ""}`}>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] text-slate-400 w-5">{String(i + 1).padStart(2, "0")}</span>
        <input data-testid={`stamp-line-input-${i}`} value={line.text} maxLength={120} placeholder={missing ? "Testo mancante" : `Scrivi la riga ${i + 1}`}
          onFocus={() => line.pristine && update({ text: "", pristine: false })}
          onChange={(e) => update({ text: e.target.value })}
          className={`flex-1 min-w-0 h-9 px-3 rounded-md border bg-white focus:outline-none focus:ring-2 focus:ring-brand-yellow/70 text-sm ${missing ? "border-red-300 placeholder:text-red-400" : "border-slate-200"}`} />
        <button data-testid={`line-up-${i}`} className={ib(false, i === 0)} disabled={i === 0} onClick={() => moveBy(-1)} title="Sposta su"><ArrowUp className="w-3.5 h-3.5" /></button>
        <button data-testid={`line-down-${i}`} className={ib(false, i === n - 1)} disabled={i === n - 1} onClick={() => moveBy(1)} title="Sposta giù"><ArrowDown className="w-3.5 h-3.5" /></button>
        <button data-testid={`line-delete-${i}`} disabled={n <= 1} onClick={(e) => { e.stopPropagation(); remove(); }} title="Elimina riga"
          className={`press w-8 h-8 inline-flex items-center justify-center rounded border border-red-200 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 ${n <= 1 ? "opacity-30 cursor-not-allowed" : ""}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      {line.pristine && (
        <p data-testid={`line-sample-${i}`} className="pl-7 flex items-center gap-1 text-[11px] font-semibold text-amber-700">
          <AlertCircle className="w-3 h-3" /> Testo di esempio: clicca e sostituiscilo con i tuoi dati, altrimenti verrà cancellato
        </p>
      )}
      {missing && (
        <p data-testid={`line-missing-${i}`} className="pl-7 flex items-center gap-1 text-[11px] text-red-600">
          <AlertCircle className="w-3 h-3" /> Testo mancante: scrivi qualcosa oppure elimina la riga
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5 pl-7">
        <select data-testid={`line-font-${i}`} value={line.font} onChange={(e) => update({ font: e.target.value, bold: false, italic: false })}
          className="h-8 rounded border border-slate-200 bg-white text-xs px-2 max-w-[150px]">
          {fonts.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
        <div className="flex items-center border border-slate-200 rounded h-8 bg-white">
          <button data-testid={`line-size-dec-${i}`} className="w-7 h-full flex items-center justify-center hover:bg-slate-100" onClick={() => setSize(line.size_pt - 0.5)}><Minus className="w-3 h-3" /></button>
          <span data-testid={`line-size-${i}`} className="font-mono text-xs w-12 text-center">{line.size_pt}pt</span>
          <button data-testid={`line-size-inc-${i}`} className="w-7 h-full flex items-center justify-center hover:bg-slate-100" onClick={() => setSize(line.size_pt + 0.5)}><Plus className="w-3 h-3" /></button>
        </div>
        <button data-testid={`line-bold-${i}`} className={ib(line.bold, !canBold)} disabled={!canBold} onClick={() => update({ bold: !line.bold, italic: false })}><Bold className="w-3.5 h-3.5" /></button>
        <button data-testid={`line-italic-${i}`} className={ib(line.italic, !canItalic)} disabled={!canItalic} onClick={() => update({ italic: !line.italic })}><Italic className="w-3.5 h-3.5" /></button>
        <button data-testid={`line-underline-${i}`} className={ib(line.underline, !!line.curve)} disabled={!!line.curve} onClick={() => update({ underline: !line.underline })}><Underline className="w-3.5 h-3.5" /></button>
        {!line.curve && (
          <span className="inline-flex gap-0.5">
            {[["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]].map(([a, Icon]) => (
              <button key={a} data-testid={`line-align-${a}-${i}`} className={ib(line.align === a)} onClick={() => update({ align: a, offset_x: 0 })}><Icon className="w-3.5 h-3.5" /></button>
            ))}
          </span>
        )}
        {cfg.allow_curved_text && (
          <select data-testid={`line-curve-${i}`} value={line.curve || ""} onChange={(e) => update({ curve: e.target.value || null, offset_x: 0, offset_y: 0, underline: false })}
            className="h-8 rounded border border-slate-200 bg-white text-xs px-2">
            <option value="">Dritto</option>
            <option value="top">Arco sopra</option>
            <option value="bottom">Arco sotto</option>
          </select>
        )}
        {fitted && <span data-testid={`line-fitted-${i}`} className="font-mono text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">adattato {fitted}pt</span>}
      </div>
    </div>
  );
}

export function TextLinesPanel({ product, design, setDesign, selected, setSelected, warnings, layout }) {
  const meta = useMeta();
  const cfg = product.config;
  const allowed = cfg.allowed_fonts?.length ? cfg.allowed_fonts : null;
  const fonts = (meta?.fonts || []).filter((f) => !allowed || allowed.includes(f.id));
  const max = maxLines(cfg);
  const setLines = (fn) => setDesign((d) => ({ ...d, lines: fn(d.lines) }));
  const canAdd = cfg.customer_can_add_lines && design.lines.length < max;
  const fitted = {};
  layout?.elements.forEach((e) => { if (e.fitted) fitted[e.ref.i] = e.size_pt; });

  return (
    <div className="space-y-3" data-testid="text-lines-panel">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xl font-bold uppercase tracking-wide">Righe di testo</h3>
        <span className="font-mono text-[11px] text-slate-500" data-testid="lines-counter">{design.lines.length}/{max} righe</span>
      </div>
      {design.lines.map((l, i) => (
        <LineCard key={l.id} line={l} i={i} n={design.lines.length} cfg={cfg} fonts={fonts}
          active={selected?.kind === "line" && selected.i === i} fitted={fitted[i]} warn={warnings.includes(`line-${i}`)}
          onFocus={() => setSelected({ kind: "line", i })}
          update={(p) => setLines((ls) => ls.map((x, k) => (k === i ? { ...x, ...p } : x)))}
          remove={() => { setSelected(null); setLines((ls) => ls.filter((_, k) => k !== i)); }}
          moveBy={(d) => setLines((ls) => { const a = [...ls]; [a[i], a[i + d]] = [a[i + d], a[i]]; return a; })} />
      ))}
      <button data-testid="add-line-btn" disabled={!canAdd} onClick={() => setLines((ls) => [...ls, newLine(cfg)])}
        className="press w-full h-10 rounded-lg border-2 border-dashed border-slate-300 text-sm font-semibold text-slate-600 hover:border-brand-navy hover:text-brand-navy disabled:opacity-40 disabled:hover:border-slate-300 disabled:hover:text-slate-600 flex items-center justify-center gap-2">
        <Plus className="w-4 h-4" /> {canAdd ? "Aggiungi riga" : cfg.customer_can_add_lines ? `Limite di ${max} righe raggiunto` : "Numero righe fisso per questo modello"}
      </button>
      {cfg.extra_line_price > 0 && <p className="text-[11px] text-slate-500">Ogni riga oltre le {cfg.default_lines.length} incluse: +{cfg.extra_line_price.toFixed(2)} €</p>}
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input type="checkbox" data-testid="autofit-checkbox" className="accent-brand-navy" checked={design.autofit !== false}
          onChange={(e) => setDesign((d) => ({ ...d, autofit: e.target.checked }))} />
        Adatta automaticamente il testo allo spazio disponibile
      </label>
      <button data-testid="center-align-btn" onClick={() => setDesign(centerAll)}
        className="press w-full h-10 rounded-lg bg-brand-navy text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-brand-navy/90">
        <Crosshair className="w-4 h-4" /> Centra e allinea
      </button>
    </div>
  );
}
